import classNames from "classnames";
import * as React from "react";
import KuikaDrawnCheck from "../kuika-drawn-check/kuika-drawn-check";
import KuikaNumber from "../kuika-number/kuika-number";
import "./kuika-filter-chips.scss";

export interface IKuikaFilterChipOption {
  label: string;
  value: string;
  /** How many items carry this value. Shown beside the label when present. */
  count?: number;
}

export interface IKuikaFilterChipsProps {
  options: IKuikaFilterChipOption[];
  /** Selected values. Empty means "everything", which lights the leading chip. */
  value: string[];
  onChange: (value: string[]) => void;
  /** Leading chip that clears the selection. Pass null to omit it. */
  allLabel?: string | null;
  allCount?: number;
  /** Names the group for screen readers — "Category", "Tag", "Status". */
  groupLabel: string;
  className?: string;
  "data-testid"?: string;
}

const ALL_CHIP_KEY: string = "__all";

const isAllSelected = (selection: string[]): boolean => selection.length === 0;

const isSameSelection = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((item: string): boolean => right.includes(item));

/** Keys of the chips that are on in `next` but were off in `previous` (the All chip included). */
const chipKeysTurnedOn = (previous: string[], next: string[]): string[] => {
  const keys: string[] = next.filter((item: string): boolean => !previous.includes(item));
  if (isAllSelected(next) && !isAllSelected(previous)) keys.push(ALL_CHIP_KEY);
  return keys;
};

/**
 * A row of toggleable filter pills.
 *
 * Use it instead of a multi-select when the option set is small enough to show and
 * worth showing: a dropdown hides both what the categories ARE and how many things
 * are in each, which is exactly the question someone browsing a catalogue is asking.
 * Selection is OR, so an item tagged twice still appears once.
 *
 * A chip turning on opens a ✓ slot and draws the check; counts roll with KuikaNumber. Neither
 * animates on the first render.
 */
const KuikaFilterChips: React.FC<IKuikaFilterChipsProps> = ({
  options,
  value,
  onChange,
  allLabel = "All",
  allCount,
  groupLabel,
  className,
  "data-testid": dataTestId
}: IKuikaFilterChipsProps) => {
  // The chips the last selection change turned on, derived from the previous `value` during render
  // (React's documented pattern). They keep drawing their check until the next change, so a
  // re-render in between never cuts it; the first render turns nothing on.
  const [previousValue, setPreviousValue] = React.useState<string[]>(value);
  const [turnedOn, setTurnedOn] = React.useState<string[]>([]);
  if (!isSameSelection(previousValue, value)) {
    setPreviousValue(value);
    setTurnedOn(chipKeysTurnedOn(previousValue, value));
  }

  const toggle = (optionValue: string): void => {
    const next: string[] = value.includes(optionValue)
      ? value.filter((v: string) => v !== optionValue)
      : [...value, optionValue];
    onChange(next);
  };

  const renderChip = (
    key: string,
    label: string,
    count: number | undefined,
    isSelectedIn: (selection: string[]) => boolean,
    onClick: () => void,
    testId: string
  ): React.ReactNode => {
    const selected: boolean = isSelectedIn(value);
    const checking: boolean = selected && turnedOn.includes(key);
    return (
      <button
        key={key}
        type="button"
        className={classNames("kuika-filter-chips__chip", {
          "kuika-filter-chips__chip--selected": selected
        })}
        aria-pressed={selected}
        onClick={onClick}
        data-testid={testId}
      >
        <span className="kuika-filter-chips__check" aria-hidden="true">
          <KuikaDrawnCheck draw={checking} />
        </span>
        <span className="kuika-filter-chips__label">{label}</span>
        {count !== undefined && <KuikaNumber value={count} className="kuika-filter-chips__count" />}
      </button>
    );
  };

  return (
    <div
      className={classNames("kuika-filter-chips", className)}
      role="group"
      aria-label={groupLabel}
      data-testid={dataTestId}
    >
      {allLabel !== null &&
        renderChip(ALL_CHIP_KEY, allLabel, allCount, isAllSelected, (): void => onChange([]), "filterChipAll")}
      {options.map((option: IKuikaFilterChipOption): React.ReactNode =>
        renderChip(
          option.value,
          option.label,
          option.count,
          (selection: string[]): boolean => selection.includes(option.value),
          (): void => toggle(option.value),
          `filterChip-${option.value}`
        )
      )}
    </div>
  );
};

export default KuikaFilterChips;
